import React, { FC, useEffect, useState } from 'react'
import { Grid, TextField, InputAdornment, SelectChangeEvent, Box } from '@mui/material'
import {
    Search,
} from '@mui/icons-material';
import SelectInput from '../SelectInput'
import { debounce } from 'throttle-debounce'
import { useIsMounted } from 'usehooks-ts'


export interface GroupSortFilterCallback {
    searchFilter: string,
    groupBy: string,
    sortBy: string,
    sortDir: string
}

interface SelectInputProps {
    label: string,
    value: string
}

export interface GroupSortFilterProps {
    /** the callback function that triggers any time a change is made: filter, group, or sort */
    filterCallback?: (input: GroupSortFilterCallback) => void,
    /** an array of label/value pairs to group by */
    groupByOption?: SelectInputProps[],
    /** an array of label/value pairs to sort by */
    sortByOption?: SelectInputProps[],
    /** default ascending */
    sortDir?: 'asc' | 'desc',
    /** the number of milliseconds that the form will wait to trigger filterCallback after a change is made */
    debounceTime?: number,
    /** displays the filter field name to users & screen readers */
    searchLabel?: string,
    /** this should be an example of what a user might type into the filter to search */
    searchPlaceholder?: string,
    /** displays the group by field name to users & screen readers */
    groupByLabel?: string,
    /** displays the sort by field name to users & screen readers */
    sortByLabel?: string,
}

const styles = {
    icon: {
        color: 'rgba(0, 0, 0, 0.54)'
    }
};

const SortDir = Object.freeze({
    ASC: 'asc',
    DESC: 'desc'
});

/**
 * Deprecated sorting and grouping component
 * @param groupByOption
 * @param sortByOption
 * @param sortDir
 * @param debounceTime
 * @param searchLabel
 * @param searchPlaceholder
 * @param groupByLabel
 * @param sortByLabel
 * @param filterCallback
 * @constructor
 * @deprecated
 */
const GroupSortFilter: FC<GroupSortFilterProps> = ({
    groupByOption = [],
    sortByOption= [],
    sortDir = SortDir.ASC,
    debounceTime = 500,
    searchLabel = 'Filter',
    searchPlaceholder = 'Type to filter results',
    groupByLabel = 'Group By',
    sortByLabel = 'Sort By',
    filterCallback = () => { },
}) => {

    const [searchFilter, setSearchFilter] = useState<string>('');
    const [groupBy, setGroupBy] = useState<string>(groupByOption.length ? groupByOption[0].value : '');
    const [sortBy, setSortBy] = useState<string>(sortByOption.length ? sortByOption[0].value : '');
    const [orderDir, setOrderDir] = useState<string>(sortDir);
    const isMounted = useIsMounted()


    const onChangeCallBack = (
        e: SelectChangeEvent,
        setValue: React.Dispatch<React.SetStateAction<string>>
    ) => {
        const value = e.target.value;
        setValue(value);
    };

    const onClickPrev = (
        setValue: React.Dispatch<React.SetStateAction<string>>,
        selectData: SelectInputProps[],
        value: string
    ) => {
        const index = selectData.findIndex(item => item.value === value);
        const prevIndex = index === 0 ? selectData.length - 1 : index - 1;
        const prevValue = selectData[prevIndex].value;
        setValue(prevValue);
    };

    const onClickNext = (
        setValue: React.Dispatch<React.SetStateAction<string>>,
        selectData: SelectInputProps[],
        value: string
    ) => {
        const index = selectData.findIndex(item => item.value === value);
        const nextIndex = index === selectData.length - 1 ? 0 : index + 1;
        const nextValue = selectData[nextIndex].value;
        setValue(nextValue);
    };

    useEffect(() => {
        const debounced = debounce(Number.isNaN(debounceTime) ? 500 : debounceTime, filterCallback);
        if (isMounted()) {
            debounced({ searchFilter, groupBy, sortBy, sortDir: orderDir });
        }
        return debounced.cancel;
    }, [searchFilter, groupBy, sortBy, orderDir, debounceTime, filterCallback, isMounted]);


    return (
        <Box>
            <Grid container mt={2} spacing={2} sx={{
                '& .MuiFormControl-root.MuiFormControl-fullWidth': {
                    mt: 0,
                    mb: 0
                }
            }}>
                <Grid item xs={12} md={6}>
                    <TextField
                        variant='standard'
                        type='text'
                        InputLabelProps={{
                            shrink: true,
                        }}
                        value={searchFilter}
                        onChange={(e) => setSearchFilter(e.target.value)}
                        placeholder={searchPlaceholder}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Search fontSize='medium' sx={styles.icon} />
                                </InputAdornment>
                            ),
                        }}
                        fullWidth
                        label={searchLabel}
                        sx={{
                            '& input::placeholder': {
                                fontSize: '1em'
                            }
                        }}
                    />
                </Grid>
                {
                  groupByOption.length ? <Grid item xs={12} md>
                      <SelectInput
                          label={groupByLabel}
                          id='group-by'
                          selectData={groupByOption}
                          value={groupBy}
                          hasAction={true}
                          onChange={(e) => onChangeCallBack(e, setGroupBy)}
                          onNextClick={() => onClickNext(setGroupBy, groupByOption, groupBy)}
                          onPrevClick={() => onClickPrev(setGroupBy, groupByOption, groupBy)}
                        />
                    </Grid> : ""
                }
                {
                    sortByOption.length ? <Grid item xs={12} md>
                        <SelectInput
                          label={sortByLabel}
                          id='sort-by'
                          selectData={sortByOption}
                          value={sortBy}
                          hasAction={true}
                          onChange={(e) => onChangeCallBack(e, setSortBy)}
                          onNextClick={() => setOrderDir(SortDir.ASC)}
                          onPrevClick={() => setOrderDir(SortDir.DESC)}
                          activeState={orderDir === 'asc'}
                          handleToggle={() => setOrderDir(orderDir === SortDir.ASC ? SortDir.DESC : SortDir.ASC)}
                        />
                    </Grid> : ""
                }
            </Grid>
        </Box>
    )
}



export default GroupSortFilter